import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../lib/api';

const signupSchema = z.object({
  firstname: z.string().min(1, 'First name is required').max(100),
  lastname: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmpassword: z.string().min(8, 'Password must be at least 8 characters'),
}).refine((data) => data.password === data.confirmpassword, {
  message: 'Passwords do not match',
  path: ['confirmpassword'],
});

type SignupForm = z.infer<typeof signupSchema>;

export function SignupPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  });

  async function onSubmit(data: SignupForm) {
    try {
      setError('');
      const response = await api.post('/api/security/register', data);
      const result = response.data;
      if (result.identity) {
        navigate('/');
        return;
      }
      // Registration succeeded but requires approval
      setSubmitted(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = err as { response?: { data?: { error?: string } } };
        setError(resp.response?.data?.error || 'Network error — please try again');
      } else {
        setError(message || 'Network error — please try again');
      }
    }
  }

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Registration Submitted</CardTitle>
            <CardDescription>
              Your registration requires admin approval. You'll be notified once approved.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/login" className="text-[#cc0404] hover:underline">
              Return to login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/assets/logo.png" alt="Connectfy" className="h-12 mx-auto mb-4" />
          <CardTitle>Create Account</CardTitle>
          <CardDescription>Sign up for Connectfy Portal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-[#cc0404] p-3 rounded-[4px] text-sm">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstname">First Name</Label>
                <Input id="firstname" placeholder="John" {...register('firstname')} />
                {errors.firstname && (
                  <p className="text-sm text-[#cc0404]">{errors.firstname.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastname">Last Name</Label>
                <Input id="lastname" placeholder="Doe" {...register('lastname')} />
                {errors.lastname && (
                  <p className="text-sm text-[#cc0404]">{errors.lastname.message}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input id="signup-email" type="email" placeholder="you@example.com" {...register('email')} />
              {errors.email && <p className="text-sm text-[#cc0404]">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input id="signup-password" type="password" placeholder="••••••••" {...register('password')} />
              {errors.password && <p className="text-sm text-[#cc0404]">{errors.password.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmpassword">Confirm Password</Label>
              <Input id="confirmpassword" type="password" placeholder="••••••••" {...register('confirmpassword')} />
              {errors.confirmpassword && <p className="text-sm text-[#cc0404]">{errors.confirmpassword.message}</p>}
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </Button>
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#cc0404] hover:underline">Sign in</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
